import { useTranslation } from 'react-i18next'
import {
  MessageSquare,
  GitBranch,
  FileDiff,
  CheckCircle2,
  ClipboardList,
  AlertTriangle,
  BarChart3,
  Download,
} from 'lucide-react'

export type NegotiationTab =
  | 'comments'
  | 'versions'
  | 'changes'
  | 'status'
  | 'tasks'
  | 'issues'
  | 'stats'

const TAB_ICONS: Record<NegotiationTab, typeof MessageSquare> = {
  comments: MessageSquare,
  versions: GitBranch,
  changes: FileDiff,
  status: CheckCircle2,
  tasks: ClipboardList,
  issues: AlertTriangle,
  stats: BarChart3,
}

interface NegotiationTabBarProps {
  activeTab: NegotiationTab
  onTabChange: (tab: NegotiationTab) => void
  unreadCount?: number
  openTaskCount?: number
  dealBreakerCount?: number
  onExport?: () => void
  isExporting?: boolean
}

export function NegotiationTabBar({
  activeTab,
  onTabChange,
  unreadCount = 0,
  openTaskCount = 0,
  dealBreakerCount = 0,
  onExport,
  isExporting,
}: NegotiationTabBarProps) {
  const { t } = useTranslation()

  const tabs: { key: NegotiationTab; label: string }[] = [
    { key: 'comments', label: t('negotiate.tabComments', 'Comments') },
    { key: 'versions', label: t('negotiate.tabVersions', 'Versions') },
    { key: 'changes', label: t('negotiate.tabChanges', 'Changes') },
    { key: 'status', label: t('negotiate.tabStatus', 'Status') },
    { key: 'tasks', label: t('negotiate.tabTasks', 'Tasks') },
    { key: 'issues', label: t('negotiate.tabIssues', 'Issues') },
    { key: 'stats', label: t('negotiate.tabStats', 'Stats') },
  ]

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 shrink-0">
      {tabs.map((tab) => {
        const Icon = TAB_ICONS[tab.key]
        const isActive = activeTab === tab.key
        const badge =
          tab.key === 'comments' ? unreadCount :
          tab.key === 'tasks' ? openTaskCount :
          tab.key === 'issues' ? dealBreakerCount : 0

        return (
          <button
            key={tab.key}
            onClick={() => onTabChange(tab.key)}
            title={tab.label}
            className={`relative flex items-center justify-center p-1.5 rounded-md transition-all duration-150 ${
              isActive
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 shadow-sm'
                : 'text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-600 dark:hover:text-neutral-300'
            }`}
          >
            <Icon size={15} />
            {badge > 0 && (
              <span className={`absolute -top-1 -right-1 text-[8px] font-bold rounded-full px-1 min-w-[14px] h-[14px] flex items-center justify-center ${
                tab.key === 'issues'
                  ? 'bg-error-500 text-white'
                  : tab.key === 'tasks'
                    ? 'bg-amber-500 text-white'
                    : 'bg-primary-500 text-white'
              }`}>
                {badge > 9 ? '9+' : badge}
              </span>
            )}
          </button>
        )
      })}

      {/* Export button — separated to the right */}
      {onExport && (
        <>
          <div className="flex-1" />
          <button
            onClick={onExport}
            disabled={isExporting}
            title={t('negotiate.exportPdf', 'Export as PDF')}
            className="p-1.5 rounded-md text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-600 dark:hover:text-neutral-300 disabled:opacity-50 transition-all duration-150"
          >
            <Download size={15} />
          </button>
        </>
      )}
    </div>
  )
}
